<template>
  <file-manager-preview
    :preview="preview"
    :files="allFiles"
    :file-url="props.fileUrl"
    :current-id="previewFileId"
    @delete:file="deleteFile"
  />

  <div v-if="!props.disabled" :class="'uploader ' + (error && ' uploader-error ')" v-bind="getRootProps()">
    <input type="file" v-bind="getInputProps()"/>
    <p class="pa-3 text-center" v-if="isDragActive">
      {{ $t("support.file_drop_text_hover") }}
    </p>
    <p class="pa-3 text-center" v-else>{{ placeholder ?? $t("support.file_drop_text") }}</p>
  </div>

  <div v-if="newFiles.length > 0">
    <h3 class="mt-2">{{ $t("support.files_to_be_uploaded") }}</h3>
    <div v-for="(file,index) in newFiles" :key="index" class="d-flex justify-space-between py-1">
      <p>{{ file.name }}</p>
      <v-icon @click="deleteFile(file.id)" class="cancel-icon">cancel</v-icon>
    </div>
  </div>

  <v-alert
      v-if="error"
      type="error"
      variant="outlined"
      class="rounded-lg mt-4"
  >
    {{ error }}
  </v-alert>
</template>

<script setup>
import {useDropzone} from "vue3-dropzone";
import {computed, watch} from "vue";
import {ref} from "vue";
import i18n from "@/localization/i18n";
import FileManagerPreview from "@/components/files/preview/FileManagerPreview.vue";
import FormData from "form-data";
import {del, post} from "@/services/api";

const props = defineProps({
  maxFileSizeMb: {
    type: Number,
    default: 29,
  },
  minimumWidth: {
    type: Number,
    default: 0,
  },
  minimumHeight: {
    type: Number,
    default: 0,
  },
  allowedFileTypes: {
    type: Array,
    default: () => ["image/jpeg", "image/png"],
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: null
  },
  preview: {
    validator: value => ['none', 'list', 'gallery'].includes(value),
    default: 'list'
  },
  files: {
    type: Array,
    default: () => [],
  },
  uploadUrl: {
    type: String,
  },
  uploadAttribute: {
    type: String,
    default: 'file'
  },
  fileUrl: {
    type: Function,
    default: () => {},
  },
  deleteUrl: {
    type: Function,
  },
  disabled: {
    type: Boolean,
    default: false
  },
})

const emit = defineEmits(['update:files'])

const t = i18n.global.t

const errorMessages = {
  fileTooBig: t('support.fileTooBig') + ` Max. ${props.maxFileSizeMb} MB.`,
  invalidFileType: t('support.invalidFileType') + ` ${allowedFileTypeMessage(props.allowedFileTypes)}`,
  invalidDimensions: `${t('entities.invalidDimensions')} ${props.minimumWidth}x ${props.minimumHeight} px.`,
};

const {getRootProps, getInputProps, isDragActive} = useDropzone({
  onDrop: onDropFiles,
  accept: props.allowedFileTypes,
});

const error = ref("");
const previewFileId = ref(null);
const existingFiles = ref([...props.files]);
const newFiles = ref([])
const idSequence = ref(0)
const toBeDeletedIds = ref([])

const allFiles = computed(() => [...existingFiles.value, ...newFiles.value])

watch(
    () => props.files,
    (newValue) => existingFiles.value = [...newValue]
)

function checkFileSize(file) {
  return file.raw.size <= props.maxFileSizeMb * 1000000
}

function checkFileType(file) {
  return props.allowedFileTypes.some((type) => {
    if (type.includes('/')) {
      return file.raw.type === type // mime
    } else {
      return file.raw.name.endsWith(type) // extension
    }
  })
}

function allowedFileTypeMessage(allowedFileTypes) {
  const fileTypes = allowedFileTypes.map(type =>
    type.includes('/') ? type.split('/')[1] : type
  )
  return fileTypes.join(` ${t('support.or')} `)
}

async function checkDimensions(file) {
  if (props.minimumHeight === 0 && props.minimumWidth === 0) {
    return true; // no requirements on image dimensions
  }

  // temporarily preload the image to get its dimensions
  const image = new Image();
  image.src = URL.createObjectURL(file.raw)
  await new Promise(resolve =>
      image.onload = () => resolve()
  )

  return image.width >= props.minimumWidth && image.height >= props.minimumHeight
}

async function validateFile(file) {
  if (!checkFileSize(file)) {
    error.value = errorMessages.fileTooBig;
    return false;
  }

  if (!checkFileType(file)) {
    error.value = errorMessages.invalidFileType;
    return false;
  }

  if (!(await checkDimensions(file))) {
    error.value = errorMessages.invalidDimensions;
    return false;
  }

  return true;
}

async function validateFiles(files) {
  let validFiles = []

  for (const file of files) {
    if (await validateFile(file)) {
      validFiles.push(file)
    }
  }

  return validFiles
}

function wrapToObject(rawFiles) {
  // not-yet-persisted files will have a negative id and a special attribute containing the selected file from the user's device
  return rawFiles.map(file => ({
    id: --idSequence.value,
    name: file.name,
    toBeUploaded: true,
    raw: file,
  }))
}

function processDropzoneErrors(rejectReasons) {
  const errorCodes = rejectReasons.flatMap(r => r.errors?.map(e => e.code))
  if (errorCodes.includes('file-invalid-type')) {
    error.value = errorMessages.invalidFileType;
  }
}

async function onDropFiles(acceptedFiles, rejectReasons) {
  error.value = ""
  processDropzoneErrors(rejectReasons) // errors from dropzone library must be handled in advance
  const wrappedFiles = wrapToObject(acceptedFiles)
  const validFiles = await validateFiles(wrappedFiles)
  addFilesToQueue(validFiles)
  showLastFileInPreview()
  sendUpdate()
}

function addFilesToQueue(files) {
  if (files.length === 0) {
    return;
  }

  if (props.multiple) {
    newFiles.value.push(...files);
  } else {
    markExistingFilesAsRemoved()
    newFiles.value = [files[0]]
  }
}

function showLastFileInPreview() {
  previewFileId.value = newFiles.value[newFiles.value.length - 1]?.id
}

function markExistingFilesAsRemoved() {
  toBeDeletedIds.value = []
  existingFiles.value.forEach(f => f.toBeRemoved = true)
  toBeDeletedIds.value.push(...existingFiles.value.map(e => e.id))
}

function markExistingFilesAsNotRemoved() {
  existingFiles.value.forEach(f => f.toBeRemoved = false)
  toBeDeletedIds.value = toBeDeletedIds.value.filter(id => !existingFiles.value.some(ef => ef.id === id))
}

function sendUpdate() {
  emit("update:files", [...existingFiles.value]);
}

function getFileById(id) {
  return allFiles.value.find(f => f.id === id)
}

async function deleteFile(id) {
  const file = getFileById(id)

  if (!file) return;

  if (file.toBeUploaded || window.confirm(t('support.file_delete_confirm'))) {
    removeFileFromState(id)

    if (!file.toBeUploaded) {
      const url = props.deleteUrl(file)
      await del(url)
    }

    if (!props.multiple) {
      markExistingFilesAsNotRemoved()
    }

    sendUpdate()
  }
}

async function uploadFile(file, endpointUrl, data = {}) {
  let formData = new FormData();
  formData.append(props.uploadAttribute, file.raw, file.name);
  Object.keys(data).forEach(key =>
      formData.append(key, data[key])
  )

  const response = await post(
      endpointUrl ?? props.uploadUrl,
      formData,
      {"content-type": "multipart/form-data"}
  );
  const uploadedFile = response.data.items?.[0] ?? response.data.content // response can be either array or object depending on module

  // transform file from new files to existing files (from not yet uploaded state to uploaded state), file is already persisted on server
  removeFileFromState(file.id)
  uploadedFile.name = file.name // not every response have name attribute
  existingFiles.value.push(uploadedFile)
}

function removeFileFromState(id) {
  existingFiles.value = existingFiles.value.filter(f => f.id !== id)
  newFiles.value = newFiles.value.filter(f => f.id !== id)
}

function removeFilesToBeRemoved() {
  existingFiles.value = existingFiles.value.filter(f => !toBeDeletedIds.value.includes(f.id))
  newFiles.value = newFiles.value.filter(f => !toBeDeletedIds.value.includes(f.id))
}

async function upload(endpointUrl, data) {
  for (const file of newFiles.value) {
    await uploadFile(file, endpointUrl, data)
  }
  removeFilesToBeRemoved()
  sendUpdate()
}

defineExpose({ upload })
</script>

<style scoped>
.uploader {
  display: flex;
  justify-content: center;
  align-items: center;
  color: grey;
  border: 2px dotted grey;
  border-radius: 10px;
  background-color: rgb(245, 245, 245);
  cursor: pointer;
  min-height: 8em;
}

.uploader .dz-preview {
  width: 100px;
  margin: 3em 0;
}

.uploader .dz-error-message {
  opacity: 1;
  top: 0;
  border-radius: 0;
}

.uploader.uploader-error {
  border-color: #b00020;
}

.cancel-icon {
  cursor: pointer;
  color: #6c757d;
  transition: all 0.3s ease-in-out;
}

.cancel-icon:hover {
  color: #343434;
}
</style>